/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useHotDeskingFormView.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { useFixedActionBar } from '../../../hooks/useFixedActionBar';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { useSaveWithAction } from '../../../hooks/useSaveWithAction';
import { useHotDeskingData } from './useHotDeskingData';
import { useHotDeskingForm } from './useHotDeskingForm';
import { fetchHotDeskingDevices } from '../api/hotDeskingApi';
import { deleteHotDeskById, navigateToHotDeskDuplicate } from '../hotDeskingActions';
import { generateDefaultSecret } from '../utils/passwordGenerators';
import { createDefaultFormData } from '../utils/formDefaults';
import type { HotDeskDevice, HotDeskFormState } from '../types';

const LIST_PATH = '/pbx/extensions/hot-desking';

export function useHotDeskingFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get('duplicate');
    const navigate = useNavigate();
    const { t } = useI18n();
    const { isDebugModeActive } = useDebugMode();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    useFixedActionBar(true);

    const isEditMode = !!(id && id !== 'new');

    // ── Data + form hooks ────────────────────────────────────
    const { formData, setFormData, sipProfiles, dispatchLocations, isLoading, isLoadingOptions } =
        useHotDeskingData({ id, duplicateId, isEditMode, selectedDomainId: selectedDomain?.id });

    const [originalFormData, setOriginalFormData] = useState(createDefaultFormData());
    const originalDataSetRef = useRef<string | null>(null);
    const previousLoadingRef = useRef(true);

    useEffect(() => {
        const loadingJustCompleted = previousLoadingRef.current && !isLoading;
        previousLoadingRef.current = isLoading;
        if (isEditMode && loadingJustCompleted && formData.username) {
            const currentDeviceId = id || duplicateId || 'new';
            if (originalDataSetRef.current !== currentDeviceId) {
                setOriginalFormData(JSON.parse(JSON.stringify(formData)));
                originalDataSetRef.current = currentDeviceId;
            }
        }
    }, [isLoading, isEditMode, formData.username, id, duplicateId, formData]);

    const { errors, setErrors, isSaving, handleSave: handleSaveForm, handleCancel: handleCancelForm, showCancelModal, setShowCancelModal, confirmCancel } =
        useHotDeskingForm({ formData, setFormData, originalFormData, isEditMode, extensionId: id });

    // ── Field change ─────────────────────────────────────────
    const handleChange = useCallback(<K extends keyof HotDeskFormState>(field: K, value: HotDeskFormState[K]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        const fieldStr = String(field);
        if (errors[fieldStr]) setErrors(prev => ({ ...prev, [fieldStr]: '' }));
    }, [setFormData, errors, setErrors]);

    // ── Save with action ─────────────────────────────────────
    const handleSaveInternal = useCallback(async () => {
        const savedData = await handleSaveForm();
        if (savedData) setOriginalFormData({ ...formData });
        if (!savedData && isEditMode && id) return { id, ...formData };
        return savedData;
    }, [handleSaveForm, formData, isEditMode, id]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: LIST_PATH,
        newPath: `${LIST_PATH}/new`,
        mode: isEditMode ? 'edit' : 'create',
        getEditPath: (saved) => `${LIST_PATH}/${saved.uuid || saved.id}`,
        getFormData: () => formData,
        storageKey: 'hotDesking:saveAndNewData',
    });

    // ── Navigation ───────────────────────────────────────────
    const handleGoToList = useCallback(() => navigate(LIST_PATH), [navigate]);
    const handleAddNew = useCallback(() => navigate(`${LIST_PATH}/new`), [navigate]);
    const handleDuplicateFromForm = useCallback(() => {
        if (!isEditMode || !id) return;
        navigateToHotDeskDuplicate(navigate, id);
    }, [isEditMode, id, navigate]);

    // ── Delete ───────────────────────────────────────────────
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteClick = useCallback(() => {
        if (!isEditMode || !id) return;
        setShowDeleteModal(true);
    }, [isEditMode, id]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!isEditMode || !id) return;
        setIsDeleting(true);
        try {
            await deleteHotDeskById(id);
            showSuccess(t('hotDesking.messages.deleteSuccess'));
            navigate(LIST_PATH);
        } catch (error: unknown) {
            showError(error instanceof Error ? error.message : t('hotDesking.messages.deleteError'));
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    }, [isEditMode, id, navigate, showError, showSuccess, t]);

    // ── Quick-list ───────────────────────────────────────────
    const [showQuickList, setShowQuickList] = useState(false);
    const [quickListDevices, setQuickListDevices] = useState<HotDeskDevice[]>([]);
    const [initialDevices, setInitialDevices] = useState<HotDeskDevice[]>([]);
    const [quickListSearch, setQuickListSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);

    const loadInitialDevices = useCallback(async () => {
        if (initialDevices.length > 0) return;
        setIsLoadingInitial(true);
        try {
            const result = await fetchHotDeskingDevices({ page: 1, limit: 100, sortBy: 'username', sortOrder: 'asc' });
            setInitialDevices(result.data);
            setQuickListDevices(result.data);
        } catch { setInitialDevices([]); setQuickListDevices([]); }
        finally { setIsLoadingInitial(false); }
    }, [initialDevices.length]);

    const loadQuickListDevices = useCallback(async (search: string) => {
        const normalized = search.trim();
        if (!normalized || normalized.length < 2) { setQuickListDevices(initialDevices); return; }
        setIsSearching(true);
        try {
            const result = await fetchHotDeskingDevices({ page: 1, limit: 50, sortBy: 'username', sortOrder: 'asc', search: normalized });
            setQuickListDevices(result.data);
        } catch { setQuickListDevices([]); }
        finally { setIsSearching(false); }
    }, [initialDevices]);

    useEffect(() => {
        if (showQuickList && initialDevices.length === 0) loadInitialDevices();
    }, [showQuickList, initialDevices.length, loadInitialDevices]);

    useEffect(() => {
        if (!showQuickList) { setQuickListSearch(''); return; }
        const timer = setTimeout(() => {
            if (quickListSearch.trim().length >= 2) loadQuickListDevices(quickListSearch);
            else setQuickListDevices(initialDevices);
        }, 800);
        return () => clearTimeout(timer);
    }, [quickListSearch, showQuickList, loadQuickListDevices, initialDevices]);

    // ── UI helpers ───────────────────────────────────────────
    const buildTooltipContent = useCallback((tooltipKey: string, debugKey?: string) => {
        if (!isDebugModeActive || !debugKey) return t(tooltipKey);
        return `${t(tooltipKey)}\n\n${t('extensions.debug.field')}: ${t(debugKey)}`;
    }, [isDebugModeActive, t]);

    const handleGeneratePassword = useCallback(() => handleChange('password', generateDefaultSecret()), [handleChange]);

    const closeQuickList = useCallback(() => { setShowQuickList(false); setQuickListSearch(''); }, []);
    const navigateToDevice = useCallback((device: HotDeskDevice) => {
        closeQuickList();
        navigate(`${LIST_PATH}/${device.uuid || device.id}`);
    }, [closeQuickList, navigate]);

    return {
        // Params
        id, isEditMode, duplicateId,
        // Form state
        formData, errors, isSaving, isLoading, isLoadingOptions,
        sipProfiles, dispatchLocations,
        // Form actions
        handleChange, handleSaveWithAction, handleGeneratePassword,
        handleCancelForm, handleGoToList, handleAddNew, handleDuplicateFromForm,
        showCancelModal, setShowCancelModal, confirmCancel,
        // Delete
        showDeleteModal, isDeleting, handleDeleteClick, handleDeleteConfirm,
        setShowDeleteModal,
        // Quick-list
        showQuickList, quickListDevices, quickListSearch, isSearching, isLoadingInitial,
        setShowQuickList, setQuickListSearch, closeQuickList, navigateToDevice,
        // Helpers
        buildTooltipContent, t,
    };
}
